import axios from 'axios';
import BaseApi from './base-api';

class UserSignaturesApi extends BaseApi {
  /**
   * Get all signatures for the current user
   * @returns {Promise}
   */
  getUserSignatures() {
    return axios
      .get(this.baseUrl + '/user_signatures')
      .then((response) => ({
        ok: true,
        data: response.data,
      }))
      .catch((error) => ({
        ok: false,
        errors: (error.response &&
          error.response.data &&
          error.response.data.errors) || ['Failed to load signatures'],
      }));
  }

  /**
   * Get a specific signature by ID
   * @param {number} signatureId
   * @returns {Promise}
   */
  getUserSignature(signatureId) {
    return axios
      .get(this.baseUrl + `/user_signatures/${signatureId}`)
      .then((response) => ({
        ok: true,
        data: response.data,
      }))
      .catch((error) => ({
        ok: false,
        errors: (error.response &&
          error.response.data &&
          error.response.data.errors) || ['Failed to load signature'],
      }));
  }

  /**
   * Create a new signature
   * @param {File} signatureImage - Image file
   * @param {string} label - Optional label
   * @param {boolean} isDefault - Whether to set as default
   * @returns {Promise}
   */
  createUserSignature(signatureImage, label = null, isDefault = false) {
    const formData = new FormData();
    formData.append('user_signature[signature_image]', signatureImage);
    if (label) {
      formData.append('user_signature[label]', label);
    }
    if (isDefault) {
      formData.append('user_signature[is_default]', isDefault);
    }

    return axios
      .post(this.baseUrl + '/user_signatures', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => ({
        ok: true,
        data: response.data,
      }))
      .catch((error) => ({
        ok: false,
        errors: (error.response &&
          error.response.data &&
          error.response.data.errors) || ['Failed to create signature'],
      }));
  }

  /**
   * Update a signature
   * @param {number} signatureId
   * @param {Object} updates - { signatureImage, label, isDefault }
   * @returns {Promise}
   */
  updateUserSignature(signatureId, updates = {}) {
    const formData = new FormData();
    if (updates.signatureImage) {
      formData.append(
        'user_signature[signature_image]',
        updates.signatureImage
      );
    }
    if (updates.label !== undefined) {
      formData.append('user_signature[label]', updates.label);
    }
    if (updates.isDefault !== undefined) {
      formData.append('user_signature[is_default]', updates.isDefault);
    }

    return axios
      .put(this.baseUrl + `/user_signatures/${signatureId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => ({
        ok: true,
        data: response.data,
      }))
      .catch((error) => ({
        ok: false,
        errors: (error.response &&
          error.response.data &&
          error.response.data.errors) || ['Failed to update signature'],
      }));
  }

  /**
   * Delete a signature
   * @param {number} signatureId
   * @returns {Promise}
   */
  deleteUserSignature(signatureId) {
    return axios
      .delete(this.baseUrl + `/user_signatures/${signatureId}`)
      .then(() => ({
        ok: true,
      }))
      .catch((error) => ({
        ok: false,
        errors: (error.response &&
          error.response.data &&
          error.response.data.errors) || ['Failed to delete signature'],
      }));
  }

  /**
   * Set a signature as default
   * @param {number} signatureId
   * @returns {Promise}
   */
  setDefaultSignature(signatureId) {
    return axios
      .put(this.baseUrl + `/user_signatures/${signatureId}/set_default`)
      .then((response) => ({
        ok: true,
        data: response.data,
      }))
      .catch((error) => ({
        ok: false,
        errors: (error.response &&
          error.response.data &&
          error.response.data.errors) || ['Failed to set default signature'],
      }));
  }
}

export default new UserSignaturesApi();
